/**
 * Collapsible FAQ list. Takes the same `{ term, detail }` shape as SpecList,
 * so FAQ_ITEMS and SERVICE_FAQ are used unchanged.
 *
 * ⚠️ Native <details>/<summary>, deliberately. Do not replace this with a
 * useState accordion.
 *
 * A JS accordion renders the answer only when open, so prerender.js would
 * snapshot the questions and none of the answers — the single highest-value
 * block of text on the site for answer engines would vanish from the static
 * HTML while still looking perfect in a browser. <details> keeps the answer in
 * the DOM permanently and lets the browser handle visibility: crawlers read it,
 * the FAQPage schema still matches visible content, and Ctrl+F reaches inside
 * closed answers.
 *
 * It also supplies keyboard operation, aria-expanded, and screen-reader
 * announcement for free.
 *
 * Not a <dl> like SpecList: term-definition and disclosure are different
 * semantics, and one component serving both would serve neither well.
 */
export default function FaqList({ items, className = "" }) {
  return (
    <div className={className}>
      {items.map(({ term, detail }) => (
        <details
          key={term}
          className="group border-t border-line first:border-t-0"
        >
          {/* The global *:focus-visible rule in index.css supplies the keyboard
              outline. list-none plus the webkit marker reset kill the default
              disclosure triangle on every engine. */}
          <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-6 py-4 text-sm font-medium text-white/90 [&::-webkit-details-marker]:hidden">
            <span>{term}</span>
            {/* State, not decoration: a + that becomes a − on open. */}
            <span aria-hidden className="relative block h-3 w-3 shrink-0">
              <span className="absolute left-0 top-1/2 h-px w-3 -translate-y-1/2 bg-white/45 transition-colors duration-300 group-open:bg-[#10B981]" />
              <span className="absolute left-1/2 top-0 h-3 w-px -translate-x-1/2 bg-white/45 transition-transform duration-300 group-open:rotate-90" />
            </span>
          </summary>
          <p className="max-w-[70ch] pb-5 text-sm leading-relaxed text-white/[0.62]">
            {detail}
          </p>
        </details>
      ))}
    </div>
  );
}
